import React, { useEffect, useState, useMemo } from 'react';
import { Card, Spin, Steps, Button, Input } from 'antd';
import type { BotCreate, BotUpdate } from '../types/bot.types';
import { useBotForm } from '../hooks/use-bot-form.hook';

import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Switch } from 'antd';
import { useJobList } from '../../jobs/hooks/use-job.hook';
import type { Job } from '../../jobs/types/job.types';
import type { PaginatedData } from '../../../../../types/pagination.types';
import PaginatedTable, { ColumnConfig } from '../../../../common/PaginatedTable';

interface BotFormProps {
  defaultValues?: DefaultValues<BotCreate | BotUpdate>;
  onFormSubmit: (data: BotCreate | BotUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function BotFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: BotFormProps) {
  const { t } = useTranslation();

  const {
    control,
    errors,
    resetForm,
    formSubmitHandler,
    reset,
    setError,
    trigger,
    setValue,
    watch,
  } = useBotForm({
    defaultValues,
    onSubmit: (data) => onFormSubmit(data, resetForm),
  });

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Watched values for validation/UI
  const watchedName = watch('name') as string | undefined;
  const watchedJobId = watch('jobId') as string | undefined;

  // Step 1 fields: basic bot configuration
  const step1Fields: FormField<BotCreate | BotUpdate>[] = [
    {
      name: 'name',
      label: t('bots.name.label'),
      required: true,
      errorMsg: errors?.name?.message as string | undefined,
      rules: { required: t('common.required') },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('bots.name.placeholder')} />
      ),
    },
    {
      name: 'isVideoRecEnable',
      label: t('bots.isVideoRecEnable.label'),
      required: false,
      errorMsg: errors?.isVideoRecEnable?.message as string | undefined,
      rules: {},
      render: (field) => <Switch {...field} checked={field.value || false} disabled={isLoading} />,
    },
  ];

  // Step 2: job selection
  const [jobSearch, setJobSearch] = useState('');
  const [jobPage, setJobPage] = useState(1);
  const [jobPageSize, setJobPageSize] = useState(10);

  const { data: jobData, isLoading: isJobsLoading } = useJobList({
    page: jobPage,
    limit: jobPageSize,
    searchString: jobSearch,
  });

  const jobTableData = (jobData as PaginatedData<Job> | undefined)?.docs || [];

  const jobColumns: ColumnConfig<Job>[] = useMemo(
    () => [
      {
        title: t('jobs.jobName.label', { defaultValue: 'Job Name' }),
        dataIndex: 'jobName',
        key: 'jobName',
      },
      {
        title: t('jobs.status.label', { defaultValue: 'Status' }),
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: t('jobs.total.label', { defaultValue: 'Total' }),
        dataIndex: 'total',
        key: 'total',
      },
    ],
    [t]
  );

  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

  // When editing, pre-select the existing job in the table
  useEffect(() => {
    if (watchedJobId && watchedJobId !== selectedJobId) {
      setSelectedJobId(watchedJobId);
    }
  }, [watchedJobId, selectedJobId]);

  const handleJobPageChange = (page: number) => {
    setJobPage(page);
  };

  const handleJobPageSizeChange = (size: number) => {
    setJobPageSize(size);
  };

  const step2Fields: FormField<BotCreate | BotUpdate>[] = [
    {
      name: 'jobId',
      label: t('bots.job.label', { defaultValue: 'Job' }),
      required: true,
      errorMsg: errors?.jobId?.message as string | undefined,
      rules: {
        required: t('jobs.create.jobName.required', { defaultValue: 'Please select a job' }),
      },
      render: (field) => (
        <PaginatedTable<Job>
          data={jobTableData}
          columns={jobColumns}
          loading={isJobsLoading}
          pagination={jobData as PaginatedData<Job> | undefined}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handleJobPageChange}
          onPageSizeChange={handleJobPageSizeChange}
          showSearchBar={true}
          onSearchChange={(value: string) => {
            setJobPage(1);
            setJobSearch(value);
          }}
          tableProps={{
            rowSelection: {
              type: 'checkbox',
              selectedRowKeys: selectedJobId ? [selectedJobId] : [],
              onChange: (selectedRowKeys: React.Key[]) => {
                const id = (selectedRowKeys[0] as string) || undefined;
                setSelectedJobId(id);
                field.onChange(id);
                setValue('jobId', id as any, { shouldValidate: true });
              },
            },
          }}
        />
      ),
    },
  ];

  const steps = [
    {
      title: t('bots.steps.basicInfo', { defaultValue: 'Basic Bot Information' }),
      fields: step1Fields,
    },
    {
      title: t('bots.steps.attachJob', { defaultValue: 'Attach Job to Bot' }),
      fields: step2Fields,
    },
  ];

  const handleNext = async () => {
    const valid = await trigger(['name']);
    if (!valid) return;
    setCurrentStep(1);
  };

  const handlePrev = () => {
    setCurrentStep(0);
  };

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <Steps
          current={currentStep}
          items={steps.map((s) => ({ title: s.title }))}
          className="mb-4"
        />

        <FormBuilder
          formFields={steps[currentStep].fields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={currentStep === 0 ? columns : 1}
          showSubmitButton={false}
        />

        <div className="d-flex justify-content-between mt-3">
          {currentStep > 0 ? (
            <Button onClick={handlePrev} disabled={isLoading}>
              {t('common.back')}
            </Button>
          ) : (
            <span />
          )}

          {currentStep < steps.length - 1 && (
            <Button
              type="primary"
              onClick={handleNext}
              disabled={isLoading || !watchedName || !watchedName.toString().trim()}
            >
              {t('common.next', { defaultValue: 'Next' })}
            </Button>
          )}

          {currentStep === steps.length - 1 && (
            <Button
              type="primary"
              onClick={formSubmitHandler()}
              disabled={isLoading || !watchedJobId}
            >
              {t('common.save')}
            </Button>
          )}
        </div>
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: BotFormProps, nextProps: BotFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(BotFormComponent, areEqual);
